import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend } from "recharts";
import { getSeerbitFees } from "../../../redux/actions/profile";

const PRIMARY = "#a7cc48";
const BG = "#2c2d3f";

const isoDate = (d) => d.toISOString().slice(0, 10);
const naira = (n) => `₦${Number(n || 0).toLocaleString("en-NG", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Fee revenue from SeerBit wallet transfers. "Excite fee" is what we earn; the
// SeerBit fee is passed through to SeerBit.
export default function SeerbitFees() {
  const dispatch = useDispatch();
  const [from, setFrom] = useState(isoDate(new Date(Date.now() - 30 * 86400000)));
  const [to, setTo] = useState(isoDate(new Date()));
  const [groupBy, setGroupBy] = useState("day");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = async () => {
    setError("");
    const res = await dispatch(getSeerbitFees({ from, to, groupBy }));
    if (res?.success) setData(res.data);
    else setError(res?.message || "Unable to load fee analytics");
  };

  useEffect(() => { load(); }, [groupBy]); // eslint-disable-line react-hooks/exhaustive-deps

  const totals = data?.totals || {};
  const cards = [
    { label: "Excite fee earned", value: naira(totals.platformFee), accent: PRIMARY, icon: "bi bi-graph-up-arrow" },
    { label: "SeerBit fees", value: naira(totals.providerFee), accent: "#4fb0c6", icon: "bi bi-bank" },
    { label: "Total fees charged", value: naira(totals.totalFee), accent: "#9b5de5", icon: "bi bi-receipt" },
    { label: "Transfers", value: (totals.count || 0).toLocaleString(), accent: "#2ec4b6", icon: "bi bi-arrow-left-right" },
    { label: "Transfer volume", value: naira(totals.volume), accent: "#f4a261", icon: "bi bi-cash-stack" },
    { label: "Reversed (fee refunded)", value: (totals.reversedCount || 0).toLocaleString(), accent: "#e63946", icon: "bi bi-arrow-counterclockwise" },
  ];

  return (
    <div style={{ paddingBottom: 40 }}>
      <h4 style={{ color: BG, fontWeight: 800, marginBottom: 4 }}>Wallet Fee Revenue</h4>
      <p style={{ color: "#888", fontSize: 13, marginBottom: 20 }}>Fees on settled SeerBit wallet transfers.</p>

      <div style={S.filters}>
        <label style={S.label}>From <input style={S.input} type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></label>
        <label style={S.label}>To <input style={S.input} type="date" value={to} onChange={(e) => setTo(e.target.value)} /></label>
        {["day", "month"].map((g) => (
          <button key={g} style={{ ...S.pill, background: groupBy === g ? BG : "#fff", color: groupBy === g ? "#fff" : "#555" }} onClick={() => setGroupBy(g)}>
            By {g}
          </button>
        ))}
        <button style={S.primaryBtn} onClick={load}>Apply</button>
      </div>

      {error && <p style={{ color: "#e63946", fontSize: 13 }}>{error}</p>}

      <div style={S.grid}>
        {cards.map((c) => (
          <div key={c.label} style={{ ...S.stat, borderTop: `3px solid ${c.accent}` }}>
            <i className={c.icon} style={{ color: c.accent, fontSize: 18 }} />
            <div>
              <div style={{ fontSize: 17, fontWeight: 800, color: BG }}>{c.value}</div>
              <div style={{ fontSize: 11, color: "#888" }}>{c.label}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={S.card}>
        <div style={{ fontSize: 15, fontWeight: 700, color: BG, marginBottom: 14 }}>Fees over time</div>
        {data?.series?.length ? (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={data.series}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="period" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `₦${v.toLocaleString()}`} />
              <Tooltip formatter={(v) => naira(v)} />
              <Legend />
              <Bar dataKey="platformFee" name="Excite fee" stackId="fee" fill={PRIMARY} />
              <Bar dataKey="providerFee" name="SeerBit fee" stackId="fee" fill="#4fb0c6" />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <p style={{ color: "#bbb", textAlign: "center", padding: 40 }}>No settled transfers in this period</p>
        )}
      </div>
    </div>
  );
}

const S = {
  filters: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 20 },
  label: { fontSize: 13, color: "#555", display: "flex", alignItems: "center", gap: 6 },
  input: { border: "1.5px solid #e0e0e0", borderRadius: 8, padding: "6px 10px", fontSize: 13 },
  pill: { border: "1.5px solid #e0e0e0", borderRadius: 20, padding: "5px 16px", cursor: "pointer", fontSize: 12, fontWeight: 600 },
  primaryBtn: { background: PRIMARY, color: "#fff", border: "none", borderRadius: 8, padding: "8px 18px", fontWeight: 700, cursor: "pointer" },
  grid: { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(190px, 1fr))", gap: 14, marginBottom: 20 },
  stat: { background: "#fff", borderRadius: 10, padding: "14px 16px", display: "flex", alignItems: "center", gap: 12, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" },
  card: { background: "#fff", borderRadius: 10, padding: 20, boxShadow: "0 2px 10px rgba(0,0,0,0.06)" },
};
